import { isValidElement } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  /** Buď komponenta (Globe) nebo hotový element (<Globe size={14} />) — obojí se v ~50 místech používá. */
  icon?: React.ComponentType<{ className?: string; size?: number | string }> | ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  className?: string;
}

export function StatCard({
  label,
  value,
  sub,
  icon,
  variant = 'default',
  className,
}: StatCardProps) {
  const iconEl = isValidElement(icon)
    ? icon
    : icon
    ? (() => {
      const Icon = icon as React.ComponentType<{ className?: string; size?: number | string }>;
      return <Icon size={18} />;
    })()
    : null;
  const variants = {
    default: 'border-gray-800 bg-gray-900/20',
    success: 'border-green-500/20 bg-green-600/5 text-green-400',
    warning: 'border-amber-500/20 bg-amber-600/5 text-amber-400',
    error: 'border-red-500/20 bg-red-600/5 text-red-400',
    info: 'border-plex/20 bg-plex/5 text-plex',
  };

  // Ikona v jemném barevném chipu (místo holé ikony vpravo nahoře) — modernější
  // a barevně čitelnější napříč celou platformou (StatCard je v ~50 místech).
  const iconChip = {
    default: 'bg-gray-500/10 text-gray-400',
    success: 'bg-green-500/10 text-green-400',
    warning: 'bg-amber-500/10 text-amber-400',
    error: 'bg-red-500/10 text-red-400',
    info: 'bg-plex/10 text-plex',
  };

  return (
    <div className={cn('rounded-2xl border p-5', variants[variant], className)}>
      <div className='flex items-center gap-3'>
        {iconEl && (
          <div
            className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center shrink-0',
              iconChip[variant],
            )}
          >
            {iconEl}
          </div>
        )}
        <div className='min-w-0'>
          <p className='text-3xl font-extrabold text-white leading-none'>{value ?? '—'}</p>
          <span className='block text-xs font-semibold uppercase tracking-wider text-gray-500 mt-1.5'>
            {label}
          </span>
          {sub && <p className='text-xs text-gray-500 mt-1'>{sub}</p>}
        </div>
      </div>
    </div>
  );
}

interface StatsGridProps {
  children: ReactNode;
  columns?: 2 | 3 | 4;
  className?: string;
}

export function StatsGrid({ children, columns = 4, className }: StatsGridProps) {
  const cols = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-2 xl:grid-cols-4',
  };

  return (
    <div className={cn('grid gap-4', cols[columns], className)}>
      {children}
    </div>
  );
}
